/** @module @liquidefy/code-editor/terminal/terminal-session @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  TerminalSessionState,
  TerminalSessionId,
  SessionStatus,
  ShellType,
  CommandHistoryEntry,
  CommandHistoryState,
  JobInfo,
  PromptConfig,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asTerminalSessionId,
  now,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 SESSION LIFECYCLE — createSession, transitions, status checks
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Valid session status transitions.
 */
export const VALID_SESSION_TRANSITIONS: Record<SessionStatus, SessionStatus[]> = {
  'starting': ['active', 'error', 'terminated'],
  'active': ['busy', 'suspended', 'terminated', 'error'],
  'busy': ['active', 'suspended', 'terminated', 'error'],
  'suspended': ['active', 'terminated', 'error'],
  'terminated': ['starting'],
  'error': ['starting', 'terminated'],
};

/**
 * Create a new terminal session state.
 */
export function createSession(
  id: string,
  shell: ShellType,
  env?: Record<string, string>,
  cwd?: string
): EditorResult<TerminalSessionState> {
  const state: TerminalSessionState = {
    id: asTerminalSessionId(id),
    shell,
    status: 'starting',
    env: env ?? {},
    cwd: cwd ?? '',
    history: {
      entries: [],
      maxSize: 10000,
    },
    jobs: [],
    promptConfig: {
      template: DEFAULT_PROMPT_TEMPLATE,
      showGitBranch: true,
      showGitStatus: true,
      showCwd: true,
      showUser: true,
      showHost: false,
      colors: {},
    },
  };
  return ok(state, 'SESSION_CREATED', `Session created with shell ${shell}`);
}

/**
 * Transition session to 'active'.
 */
export function startSession(state: TerminalSessionState): EditorResult<TerminalSessionState> {
  return transitionSession(state, 'active');
}

/**
 * Transition session to 'busy'.
 */
export function busySession(state: TerminalSessionState): EditorResult<TerminalSessionState> {
  return transitionSession(state, 'busy');
}

/**
 * Transition session to 'suspended'.
 */
export function suspendSession(state: TerminalSessionState): EditorResult<TerminalSessionState> {
  return transitionSession(state, 'suspended');
}

/**
 * Transition session to 'terminated'.
 */
export function terminateSession(state: TerminalSessionState): EditorResult<TerminalSessionState> {
  return transitionSession(state, 'terminated');
}

/**
 * Transition session to 'error' with an error message.
 */
export function errorSession(
  state: TerminalSessionState,
  _error: string
): EditorResult<TerminalSessionState> {
  return transitionSession(state, 'error');
}

/**
 * Check if the session is active.
 */
export function isSessionActive(state: TerminalSessionState): boolean {
  return state.status === 'active' || state.status === 'busy';
}

/**
 * Check if the session is busy.
 */
export function isSessionBusy(state: TerminalSessionState): boolean {
  return state.status === 'busy';
}

/**
 * Perform a validated session status transition.
 */
function transitionSession(
  state: TerminalSessionState,
  newStatus: SessionStatus
): EditorResult<TerminalSessionState> {
  const valid = VALID_SESSION_TRANSITIONS[state.status];
  if (!valid || !valid.includes(newStatus)) {
    return err(
      'SESSION_INVALID_TRANSITION',
      `Cannot transition session from '${state.status}' to '${newStatus}'`
    );
  }
  const updated: TerminalSessionState = { ...state, status: newStatus };
  return ok(updated, 'SESSION_TRANSITIONED', `Session transitioned to ${newStatus}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 COMMAND HISTORY — add, update, search, navigate, clear, trim, stats
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a command to the history.
 */
export function addCommand(
  state: TerminalSessionState,
  command: string,
  cwd: string
): TerminalSessionState {
  const entry: CommandHistoryEntry = {
    command,
    timestamp: now(),
    cwd,
  };
  const history = addToHistory(state.history, entry);
  return { ...state, history };
}

/**
 * Update the most recent command with exit code and duration.
 */
export function updateLastCommand(
  state: TerminalSessionState,
  exitCode: number,
  duration: number
): TerminalSessionState {
  const entries = state.history.entries;
  if (entries.length === 0) return state;

  const last = entries[entries.length - 1]!;
  const updated: CommandHistoryEntry = { ...last, exitCode, duration };
  const newEntries = [...entries.slice(0, -1), updated];
  return {
    ...state,
    history: { ...state.history, entries: newEntries },
  };
}

/**
 * Search history for commands matching a query (case-insensitive substring).
 */
export function searchHistory(
  history: CommandHistoryState,
  query: string
): CommandHistoryEntry[] {
  if (!query) return [];
  const lower = query.toLowerCase();
  return history.entries.filter(e => e.command.toLowerCase().includes(lower));
}

/**
 * Navigate up in history (go to older commands).
 */
export function navigateHistoryUp(
  history: CommandHistoryState
): { history: CommandHistoryState; entry?: CommandHistoryEntry } {
  const entries = history.entries;
  if (entries.length === 0) return { history };

  const currentIdx = history.searchIndex ?? entries.length;
  const newIdx = Math.max(0, currentIdx - 1);
  const entry = entries[newIdx];

  return {
    history: { ...history, searchIndex: newIdx },
    entry,
  };
}

/**
 * Navigate down in history (go to newer commands).
 */
export function navigateHistoryDown(
  history: CommandHistoryState
): { history: CommandHistoryState; entry?: CommandHistoryEntry } {
  const entries = history.entries;
  if (entries.length === 0) return { history };

  const currentIdx = history.searchIndex ?? entries.length;
  const newIdx = Math.min(entries.length, currentIdx + 1);

  if (newIdx >= entries.length) {
    return {
      history: { ...history, searchIndex: undefined },
      entry: undefined,
    };
  }

  return {
    history: { ...history, searchIndex: newIdx },
    entry: entries[newIdx],
  };
}

/**
 * Clear all command history.
 */
export function clearHistory(history: CommandHistoryState): CommandHistoryState {
  return { ...history, entries: [], searchIndex: undefined };
}

/**
 * Trim history to the given max size, keeping most recent.
 */
export function trimHistory(
  history: CommandHistoryState,
  maxSize: number
): CommandHistoryState {
  if (history.entries.length <= maxSize) return history;
  const trimmed = history.entries.slice(history.entries.length - maxSize);
  return { ...history, entries: trimmed, searchIndex: undefined };
}

/**
 * Get the most recent N commands.
 */
export function getRecentCommands(
  history: CommandHistoryState,
  count: number
): CommandHistoryEntry[] {
  return history.entries.slice(-count);
}

/**
 * Get the most frequently used commands, sorted by frequency.
 */
export function getMostFrequentCommands(
  history: CommandHistoryState,
  count: number
): Array<{ command: string; frequency: number }> {
  const freq = new Map<string, number>();
  for (const entry of history.entries) {
    const cmd = entry.command.trim();
    freq.set(cmd, (freq.get(cmd) ?? 0) + 1);
  }
  const sorted = Array.from(freq.entries())
    .map(([command, frequency]) => ({ command, frequency }))
    .sort((a, b) => b.frequency - a.frequency);
  return sorted.slice(0, count);
}

/**
 * Compute duration statistics for commands in history.
 */
export function commandDurationStats(
  history: CommandHistoryState
): { avg: number; min: number; max: number; p95: number } {
  const durations = history.entries
    .filter(e => e.duration !== undefined)
    .map(e => e.duration!);

  if (durations.length === 0) {
    return { avg: 0, min: 0, max: 0, p95: 0 };
  }

  const sorted = [...durations].sort((a, b) => a - b);
  const sum = sorted.reduce((a, b) => a + b, 0);
  const avg = sum / sorted.length;
  const min = sorted[0]!;
  const max = sorted[sorted.length - 1]!;
  const p95Index = Math.ceil(sorted.length * 0.95) - 1;
  const p95 = sorted[Math.max(0, p95Index)]!;

  return { avg, min, max, p95 };
}

/**
 * Internal: add an entry to history, respecting maxSize.
 */
function addToHistory(
  history: CommandHistoryState,
  entry: CommandHistoryEntry
): CommandHistoryState {
  const entries = [...history.entries, entry];
  if (entries.length > history.maxSize) {
    return { ...history, entries: entries.slice(entries.length - history.maxSize), searchIndex: undefined };
  }
  return { ...history, entries, searchIndex: undefined };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 JOB MANAGEMENT — addJob, updateJobStatus, removeJob, queries
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a new job to the session.
 */
export function addJob(
  state: TerminalSessionState,
  command: string,
  pid: number
): TerminalSessionState {
  const maxId = state.jobs.reduce((m, j) => Math.max(m, j.id), 0);
  const job: JobInfo = {
    id: maxId + 1,
    command,
    status: 'running',
    pid,
    isForeground: true,
  };
  return { ...state, jobs: [...state.jobs, job] };
}

/**
 * Update the status of a job.
 */
export function updateJobStatus(
  state: TerminalSessionState,
  jobId: number,
  status: JobInfo['status']
): TerminalSessionState {
  const jobs = state.jobs.map(j =>
    j.id === jobId ? { ...j, status } : j
  );
  return { ...state, jobs };
}

/**
 * Remove a job from the session.
 */
export function removeJob(
  state: TerminalSessionState,
  jobId: number
): TerminalSessionState {
  return { ...state, jobs: state.jobs.filter(j => j.id !== jobId) };
}

/**
 * Get all running jobs.
 */
export function getRunningJobs(state: TerminalSessionState): JobInfo[] {
  return state.jobs.filter(j => j.status === 'running');
}

/**
 * Get all stopped jobs.
 */
export function getStoppedJobs(state: TerminalSessionState): JobInfo[] {
  return state.jobs.filter(j => j.status === 'stopped');
}

/**
 * Get the current foreground job.
 */
export function getForegroundJob(state: TerminalSessionState): JobInfo | undefined {
  return state.jobs.find(j => j.isForeground && j.status === 'running');
}

/**
 * Move a job to the background.
 */
export function backgroundJob(
  state: TerminalSessionState,
  jobId: number
): TerminalSessionState {
  const jobs = state.jobs.map(j =>
    j.id === jobId ? { ...j, isForeground: false } : j
  );
  return { ...state, jobs };
}

/**
 * Move a job to the foreground.
 */
export function foregroundJob(
  state: TerminalSessionState,
  jobId: number
): TerminalSessionState {
  const jobs = state.jobs.map(j => ({
    ...j,
    isForeground: j.id === jobId,
  }));
  return { ...state, jobs };
}

/**
 * Check if there are any running jobs.
 */
export function hasRunningJobs(state: TerminalSessionState): boolean {
  return state.jobs.some(j => j.status === 'running');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 ENVIRONMENT — updateEnv, removeEnv, mergeEnv, getEnv, updateCwd, expand, export
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Set or update an environment variable.
 */
export function updateEnv(
  state: TerminalSessionState,
  key: string,
  value: string
): TerminalSessionState {
  return { ...state, env: { ...state.env, [key]: value } };
}

/**
 * Remove an environment variable.
 */
export function removeEnv(
  state: TerminalSessionState,
  key: string
): TerminalSessionState {
  const env = { ...state.env };
  delete env[key];
  return { ...state, env };
}

/**
 * Merge multiple environment variables into the session.
 */
export function mergeEnv(
  state: TerminalSessionState,
  vars: Record<string, string>
): TerminalSessionState {
  return { ...state, env: { ...state.env, ...vars } };
}

/**
 * Get an environment variable value.
 */
export function getEnv(
  state: TerminalSessionState,
  key: string
): string | undefined {
  return state.env[key];
}

/**
 * Update the current working directory.
 */
export function updateCwd(
  state: TerminalSessionState,
  cwd: string
): TerminalSessionState {
  return { ...state, cwd };
}

/**
 * Expand $VAR and ${VAR} references in a string using the environment.
 */
export function expandEnvVar(
  value: string,
  env: Record<string, string>
): string {
  return value.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}|\$([A-Za-z_][A-Za-z0-9_]*)/g,
    (_match, braced, unbraced) => {
      const varName = braced ?? unbraced;
      return env[varName] ?? '';
    }
  );
}

/**
 * Export environment as an array of "KEY=VALUE" strings suitable for shell.
 */
export function exportEnv(state: TerminalSessionState): string[] {
  return Object.entries(state.env).map(([key, value]) => `${key}=${value}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PROMPT — formatPrompt, DEFAULT_PROMPT_TEMPLATE, PROMPT_VARIABLES, git info, promptLength
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Default prompt template.
 */
export const DEFAULT_PROMPT_TEMPLATE = '{cwd} $ ';

/**
 * Available prompt template variables.
 */
export const PROMPT_VARIABLES: Record<string, string> = {
  cwd: 'Current working directory',
  user: 'Current user name',
  host: 'Host name',
  branch: 'Current git branch',
  status: 'Git status indicator',
  time: 'Current time (HH:MM)',
  shell: 'Shell name',
};

/**
 * Format a prompt by substituting template variables.
 */
export function formatPrompt(
  config: PromptConfig,
  state: TerminalSessionState
): string {
  let result = config.template;

  const variables: Record<string, string> = {
    cwd: config.showCwd ? shortenPath(state.cwd) : '',
    user: config.showUser ? (state.env['USER'] ?? state.env['USERNAME'] ?? 'user') : '',
    host: config.showHost ? (state.env['HOSTNAME'] ?? state.env['COMPUTERNAME'] ?? 'localhost') : '',
    branch: '',
    status: '',
    time: formatTime(),
    shell: state.shell,
  };

  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  }

  return result;
}

/**
 * Update prompt config with git branch and status info.
 */
export function updatePromptGitInfo(
  config: PromptConfig,
  branch?: string,
  status?: string
): PromptConfig {
  // Git info is substituted at format time; we store it in colors map for retrieval
  const colors = { ...config.colors };
  if (branch !== undefined) colors['__gitBranch'] = branch;
  if (status !== undefined) colors['__gitStatus'] = status;
  return { ...config, colors };
}

/**
 * Return the visible character count of a prompt string (stripping ANSI codes).
 */
export function promptLength(prompt: string): number {
  const stripped = stripAnsi(prompt);
  return stripped.length;
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function shortenPath(path: string): string {
  if (!path) return '~';
  const home = '/Users/';
  if (path.startsWith(home) && home.length > 7) {
    return '~' + path.slice(home.length);
  }
  // Shorten to last two segments
  const parts = path.split('/').filter(Boolean);
  if (parts.length <= 2) return path;
  return '.../' + parts.slice(-2).join('/');
}

function formatTime(): string {
  const d = new Date();
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}`;
}

function stripAnsi(text: string): string {
  return text.replace(/\x1b\[[0-9;]*[a-zA-Z]|\x1b\][^\x07]*\x07/g, '');
}
